import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ProjectServices } from "apis/Services/ProjectService";
import { AuthStorage } from "utils/auth-storage";

const SUCCESS_FEEDBACK_DURATION = 4000;
const APPROVAL_FIELDS = new Set(["leadTime", "subCommittedDeliveryDate"]);
const FIELD_LABELS = {
  leadTime: "lead time",
  subCommittedDeliveryDate: "committed delivery date",
  actualDeliveryDate: "actual delivery date",
};
export const UNCONFIRMED_EDIT_MESSAGE =
  "We couldn’t confirm your change was saved.";

export function getCellEditFeedbackKey(entryId, field) {
  return `${entryId}::${field}`;
}

function requiresApproval(accessLevel, field) {
  return accessLevel === 1 && APPROVAL_FIELDS.has(field);
}

function getConfirmationMessage(fields, accessLevel, alignedData) {
  const approvalFields = fields.filter((field) =>
    requiresApproval(accessLevel, field),
  );
  if (fields.length === 1) {
    const field = fields[0];
    const label = FIELD_LABELS[field] || alignedData?.[field]?.label?.toLowerCase();
    if (label) {
      return approvalFields.length
        ? `Your ${label} change was submitted for approval.`
        : `Your ${label} was saved.`;
    }
  }
  if (approvalFields.length && approvalFields.length === fields.length) {
    return "Your changes were submitted for approval.";
  }
  if (approvalFields.length) {
    return "Your direct changes were saved. Your other changes were submitted for approval.";
  }
  return "Your changes were saved.";
}

// Tracks each edit separately and confirms it only after the save API succeeds.
export function useProcurementEditFeedback({
  companyId,
  projectId,
  accessLevel,
  alignedData,
  gridRef,
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
  setItemsPendingApprovals,
}) {
  const isSubcontractor = accessLevel === 1;
  const scope = useMemo(
    () =>
      JSON.stringify([
        companyId,
        projectId,
        AuthStorage.getItem("email"),
        isSubcontractor,
      ]),
    [companyId, projectId, isSubcontractor],
  );
  const scopeRef = useRef(scope);
  const mountedRef = useRef(false);
  const feedbackRef = useRef({});
  const timersRef = useRef(new Map());
  const queuesRef = useRef(new Map());
  const nextRequestIdRef = useRef(1);
  const refreshedFeedbackRef = useRef({});
  const [cellEditFeedback, setCellEditFeedback] = useState({});

  const updateFeedback = useCallback((update) => {
    feedbackRef.current = update(feedbackRef.current);
    setCellEditFeedback(feedbackRef.current);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    scopeRef.current = scope;
    feedbackRef.current = {};
    setCellEditFeedback({});
    queuesRef.current = new Map();
    const timers = timersRef.current;
    return () => {
      mountedRef.current = false;
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [scope]);

  const getCellEditFeedback = useCallback(
    (entryId, field) =>
      isSubcontractor
        ? feedbackRef.current[getCellEditFeedbackKey(entryId, field)]
        : undefined,
    [isSubcontractor],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    const previous = refreshedFeedbackRef.current;
    const changedKeys = new Set([
      ...Object.keys(previous),
      ...Object.keys(cellEditFeedback),
    ]);
    const changedCells = [...changedKeys]
      .filter((key) => previous[key] !== cellEditFeedback[key])
      .map((key) => cellEditFeedback[key] || previous[key]);
    refreshedFeedbackRef.current = cellEditFeedback;
    const rowNodes = [...new Set(changedCells.map((cell) => cell.entryId))]
      .map((entryId) => api.getRowNode(`submittal:${entryId}`))
      .filter(Boolean);
    if (!rowNodes.length) return;
    api.refreshCells({
      rowNodes,
      columns: [...new Set(changedCells.map((cell) => cell.field))],
      force: true,
    });
  }, [cellEditFeedback, gridRef]);

  const persistEditedRows = useCallback(
    async (changedRowUpdates, requestedRowPatches) => {
      if (!changedRowUpdates?.length) return;

      const requestScope = scope;
      const isCurrentScope = () =>
        mountedRef.current && scopeRef.current === requestScope;
      const authData = {
        company: companyId,
        project: projectId,
        company_id: companyId,
        project_id: projectId,
        code: AuthStorage.getItem("token"),
        loginEmail: AuthStorage.getItem("email"),
      };

      if (!isSubcontractor) {
        // Keep the existing save and sync-status behavior for other users.
        const syncToken = beginSyncActivity("Saving changes");
        try {
          await Promise.all(
            changedRowUpdates.map((rowUpdate) =>
              ProjectServices.procurementLogEditSingleColumn(rowUpdate, authData),
            ),
          );
          finishSyncActivity(syncToken);
        } catch (error) {
          console.error("Failed to save procurement row changes", error);
          failSyncActivity(
            syncToken,
            "Failed to save changes. You may no longer be synced to the latest version.",
          );
        }
        return;
      }

      const requests = changedRowUpdates.map((rowUpdate) => {
        const entryId = String(rowUpdate.entryID);
        const requestedPatch = requestedRowPatches?.get(entryId);
        const changedFields = Object.keys(rowUpdate).filter(
          (field) => field !== "entryID",
        );
        const requestedFields = Object.keys(requestedPatch || rowUpdate).filter(
          (field) =>
            field !== "entryID" &&
            Object.prototype.hasOwnProperty.call(rowUpdate, field),
        );
        const fields = requestedFields.length ? requestedFields : changedFields;
        const requestId = nextRequestIdRef.current++;
        const approvalFields = fields.filter((field) =>
          requiresApproval(accessLevel, field),
        );

        updateFeedback((previous) => {
          const next = { ...previous };
          fields.forEach((field) => {
            const key = getCellEditFeedbackKey(entryId, field);
            window.clearTimeout(timersRef.current.get(key));
            timersRef.current.delete(key);
            next[key] = {
              entryId,
              field,
              value: rowUpdate[field],
              requestId,
              status: requiresApproval(accessLevel, field)
                ? "submitting"
                : "saving",
            };
          });
          return next;
        });

        const settleFeedback = (failed) => {
          if (!isCurrentScope()) return;
          updateFeedback((previous) => {
            const next = { ...previous };
            fields.forEach((field) => {
              const key = getCellEditFeedbackKey(entryId, field);
              if (next[key]?.requestId !== requestId) return;
              next[key] = {
                ...next[key],
                status: failed
                  ? "unconfirmed"
                  : requiresApproval(accessLevel, field)
                    ? "submitted"
                    : "saved",
              };
              if (failed) return;
              timersRef.current.set(
                key,
                window.setTimeout(() => {
                  timersRef.current.delete(key);
                  if (!isCurrentScope()) return;
                  updateFeedback((current) => {
                    if (current[key]?.requestId !== requestId) return current;
                    const remaining = { ...current };
                    delete remaining[key];
                    return remaining;
                  });
                }, SUCCESS_FEEDBACK_DURATION),
              );
            });
            return next;
          });
        };

        const submitRequest = async () => {
          if (!isCurrentScope()) return;
          const syncToken = beginSyncActivity(
            approvalFields.length ? "Submitting change request" : "Saving changes",
          );
          try {
            await ProjectServices.procurementLogEditSingleColumn(
              rowUpdate,
              authData,
            );
            if (!isCurrentScope()) {
              if (mountedRef.current) finishSyncActivity(syncToken);
              return;
            }
            settleFeedback(false);
            if (approvalFields.length) {
              setItemsPendingApprovals((previous) =>
                previous.includes(entryId) ? previous : [...previous, entryId],
              );
            }
            const message = getConfirmationMessage(
              fields,
              accessLevel,
              alignedData,
            );
            finishSyncActivity(syncToken, message);
            toast.success(message, {
              id: `procurement-edit:${requestScope}:${entryId}`,
            });
          } catch (error) {
            if (!isCurrentScope()) {
              if (mountedRef.current) finishSyncActivity(syncToken);
              return;
            }
            console.error("Failed to confirm procurement row changes", error);
            settleFeedback(true);
            failSyncActivity(syncToken, UNCONFIRMED_EDIT_MESSAGE);
            toast.error(
              `${UNCONFIRMED_EDIT_MESSAGE} Your entered value is marked unconfirmed.`,
              { id: `procurement-edit:${requestScope}:${entryId}` },
            );
          }
        };

        // Preserve edit order when a user edits the same row before its save completes.
        const previousRequest = queuesRef.current.get(entryId) || Promise.resolve();
        const request = previousRequest.then(submitRequest, submitRequest);
        queuesRef.current.set(entryId, request);
        const clearQueue = () => {
          if (queuesRef.current.get(entryId) === request) {
            queuesRef.current.delete(entryId);
          }
        };
        request.then(clearQueue, clearQueue);
        return request;
      });
      await Promise.all(requests);
    },
    [
      accessLevel,
      alignedData,
      beginSyncActivity,
      companyId,
      failSyncActivity,
      finishSyncActivity,
      isSubcontractor,
      projectId,
      scope,
      setItemsPendingApprovals,
      updateFeedback,
    ],
  );

  return { getCellEditFeedback, persistEditedRows };
}
